import { LinkOutbound } from '@/components/common/Link'
import { ReadMoreLink } from '@/components/common/ReadMoreLink'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { ARTICLE_URLS } from '@/defs/misc'
import { type RecType } from '@/defs/rec'
import { cn } from '@/utils/commonUtil'
import { formatLift, formatPercentage } from '@/utils/formatUtil'
import { getRecName } from '@/utils/recUtil'

import { StatCommonBox } from '../../StatCommon/StatCommonBox/StatCommonBox'
import { StatCommonIconChevron } from '../../StatCommon/StatCommonIconChevron/StatCommonIconChevron'

import styles from './StatRecLift.module.scss'

type Props = {
  isInView?: boolean
  rec: RecType
}

export const StatRecLift = ({ isInView, rec }: Props) => {
  const { page } = usePage()
  const { inclusion, lift } = rec
  const fullName = getRecName(rec)
  const featuredName = page?.route?.section === 'combosFeatured' ? 'this combo' : getRecName(page?.featured?.rec)
  const context = page?.route?.section === 'commandersFeatured' ? 'commander' : 'none'

  const variant =
    lift === undefined
      ? 'neutral'
      : lift >= 100
        ? 'veryHigh'
        : lift >= 10
          ? 'high'
          : lift >= 1.25
            ? 'slightlyHigh'
            : lift >= 0.8
              ? 'neutral'
              : lift >= 0.3
                ? 'slightlyLow'
                : lift >= 0.1
                  ? 'low'
                  : 'veryLow'

  const liftFormatted = lift !== undefined ? formatLift(lift) : undefined

  const inclusionOverall = lift ? (inclusion ?? 0) / lift : 0

  const popover = lift !== undefined && (
    <>
      <h2>
        <IconWithLabel iconBefore='anglesUp' iconBeforeMargin='sm' iconBeforeSize='sm'>
          Lift
        </IconWithLabel>
      </h2>
      {liftFormatted && fullName && featuredName && (
        <>
          <p>
            <span>
              <strong>{fullName}</strong> is included{' '}
            </span>
            <span>
              {context === 'commander' ? (
                <>
                  in <strong>{featuredName}</strong> decks
                </>
              ) : (
                <>
                  in decks with <strong>{featuredName}</strong>
                </>
              )}
            </span>{' '}
            <strong>{liftFormatted} times</strong>{' '}
            <span>{lift <= 1 ? <>as often as average.</> : <>more often than average.</>}</span>
          </p>
          <div className={styles.equation}>
            <div className={styles.term}>
              <strong>Lift</strong> =
            </div>
            <div className={styles.fraction}>
              <div className={styles.fractionNumerator}>
                <strong>{formatPercentage(inclusion ?? 0)}%</strong> inclusion
              </div>
              <hr className={styles.fractionRule} />
              <div className={styles.fractionDenominator}>
                <strong>{formatPercentage(inclusionOverall)}%</strong> inclusion in{' '}
                <LinkOutbound href='/faq#potential-decks'>potential decks</LinkOutbound>
              </div>
            </div>
            <div className={styles.term}>
              = <strong>{liftFormatted}</strong>
            </div>
          </div>
        </>
      )}
      <p>
        <ReadMoreLink as='LinkOutbound' href={ARTICLE_URLS.LIFT} />
      </p>
    </>
  )

  return (
    <StatCommonBox isInView={isInView} label='Lift' popover={popover}>
      {liftFormatted !== undefined && (
        <span className={cn(styles.container, { styles, variant })}>
          <StatCommonIconChevron variant={variant} />
          <data className={styles.figure} value={lift}>
            <span className={styles.value}>{liftFormatted}</span>
          </data>
          <span className={styles.times}>
            <Icon name='xMark' />
          </span>
        </span>
      )}
    </StatCommonBox>
  )
}
